import { date } from '@ipfabric/utils';
import { Select } from 'components/Select/Select';
import type { SelectOption } from 'components/Select/SelectOption';
import { type FC, memo, useMemo } from 'react';
import { useDateTimeFormat } from 'utils/hooks/useDateTimeFormat';
import { useTimezone } from 'utils/hooks/useTimeZone';
import type { CompareManagementConfigurationConfig } from './types';

type Props = {
  leftConfig: CompareManagementConfigurationConfig;
  rightConfig: CompareManagementConfigurationConfig;
  leftConfigOptions: CompareManagementConfigurationConfig[];
  rightConfigOptions: CompareManagementConfigurationConfig[];
  onLeftConfigSelected: (config: CompareManagementConfigurationConfig) => void;
  onRightConfigSelected: (config: CompareManagementConfigurationConfig) => void;
};

export const ConfigurationDiffModalTitle: FC<Props> = memo((props) => {
  const dateTimeFormat = useDateTimeFormat();
  const timezone = useTimezone();

  const isSameSerialNumber = props.leftConfig.sn === props.rightConfig.sn;

  const fromOptionsSorted = useMemo(
    () =>
      props.leftConfigOptions
        .slice()
        .sort((a, b) => b.lastChangeAt - a.lastChangeAt),
    [props.leftConfigOptions],
  );

  const toOptionsSorted = useMemo(
    () =>
      isSameSerialNumber
        ? fromOptionsSorted
        : props.rightConfigOptions
            .slice()
            .sort((a, b) => b.lastChangeAt - a.lastChangeAt),
    [props.rightConfigOptions, isSameSerialNumber, fromOptionsSorted],
  );

  const getOptionLabel = useMemo(
    () => (config: CompareManagementConfigurationConfig, index: number) => {
      const timestamp = date.formatUtcTimestampInTimeZone(
        config.lastChangeAt,
        timezone,
        dateTimeFormat,
      );

      if (fromOptionsSorted[0] === config || toOptionsSorted[0] === config) {
        return (
          <span>
            {timestamp} <strong>(latest)</strong>
          </span>
        );
      }

      if (!isSameSerialNumber) {
        return `${timestamp}`;
      }

      const selectedFromIndex = fromOptionsSorted.findIndex(
        (x) => x.hash === props.leftConfig.hash,
      );
      if (selectedFromIndex + 1 === index) {
        return (
          <span>
            {timestamp} <strong>(previous)</strong>
          </span>
        );
      }

      return `${timestamp}`;
    },
    [
      dateTimeFormat,
      isSameSerialNumber,
      fromOptionsSorted,
      toOptionsSorted,
      timezone,
      props.leftConfig,
    ],
  );

  const fromConfigSelectOptions: SelectOption<CompareManagementConfigurationConfig>[] =
    useMemo(
      () =>
        fromOptionsSorted.map((config, i) => ({
          value: config,
          label: config.hash,
          disabled: config.hash === props.rightConfig.hash,
          content: getOptionLabel(config, i),
        })),
      [fromOptionsSorted, props.rightConfig, getOptionLabel],
    );

  const toConfigSelectOptions: SelectOption<CompareManagementConfigurationConfig>[] =
    useMemo(
      () =>
        toOptionsSorted.map((config, i) => ({
          value: config,
          label: config.hash,
          disabled: config.hash === props.leftConfig.hash,
          content: getOptionLabel(config, i),
        })),
      [toOptionsSorted, props.leftConfig, getOptionLabel],
    );

  return (
    <>
      <div className='flex gap-2 items-center'>
        <strong>
          Comparing {props.leftConfig.sn} ({props.leftConfig.hostname})
        </strong>
        <Select<CompareManagementConfigurationConfig>
          nonClearable
          className='min-w-48'
          size='tiny'
          value={props.leftConfig}
          options={fromConfigSelectOptions}
          getOptionValue={(option) => option?.hash ?? ''}
          onChange={(config: CompareManagementConfigurationConfig) => {
            if (config) {
              props.onLeftConfigSelected(config);
            }
          }}
        />
      </div>
      <div className='flex gap-2 items-center'>
        <strong>
          with
          {isSameSerialNumber
            ? null
            : ` ${props.rightConfig.sn} (${props.rightConfig.hostname})`}
        </strong>
        <Select<CompareManagementConfigurationConfig>
          nonClearable
          className='min-w-48'
          size='tiny'
          value={props.rightConfig}
          options={toConfigSelectOptions}
          getOptionValue={(option) => option?.hash ?? ''}
          onChange={(config: CompareManagementConfigurationConfig) => {
            if (config) {
              props.onRightConfigSelected(config);
            }
          }}
        />
      </div>
    </>
  );
});
